import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
    Alert,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    IconButton,
    InputAdornment,
    TextField,
} from "@mui/material";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { login } from "../../../application/actions/authActions";
import { getAuthSlice } from "../../../application/selectors/auth";
import {
    getMessageSeverity,
    getMessageSlice,
    getMessageText,
} from "../../../application/selectors/message";
import { clearMessage } from "../../../application/slices/message";
import { normalizeDigits, normalizeNumbers } from "../../../infrastructure/utils/formatters";
import {
  getAccessToken,
  isAccessTokenExpired,
  readStoredUser,
  resolvePostLoginUrl,
} from "../../../infrastructure/utils/authSession";
import { ReactComponent as UnlockIcon } from "../../assets/icons/unlock.svg";
import { ReactComponent as UserIcon } from "../../assets/icons/user.svg";
import PanelInfo from "../shared/panel-info";
import VpnLoginHint from "./VpnLoginHint";

const UserPassLogin = ({ hasBothLogin, onChangeLoginKind }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { isLoggedIn } = useSelector(getAuthSlice);
  const { message } = useSelector(getMessageSlice);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(clearMessage());
  }, [dispatch]);

  const initialValues = {
    username: "",
    password: "",
  };

  const validationSchema = Yup.object().shape({
    username: Yup.string().required("لطفا این فیلد را پر کنید."),
    password: Yup.string().required("لطفا این فیلد را پر کنید."),
  });

  const onSubmit = (formValue, { setSubmitting }) => {
    const { username, password } = formValue;

    setLoading(true);

    // Normalize digits in username/password to accept Persian/Arabic numerals
    const usernameNorm = normalizeNumbers(username);
    const passwordNorm = normalizeNumbers(password);

    dispatch(login({ username: usernameNorm, password: passwordNorm }))
      .unwrap()
        .then(({ user }) => {
          navigate(resolvePostLoginUrl(from, user), { replace: true });
      })
      .catch(() => {
        setLoading(false);
        setSubmitting(false);
      });
  };

  const storedUser = readStoredUser();
  if (
    isLoggedIn &&
    getAccessToken(storedUser) &&
    !isAccessTokenExpired(storedUser)
  ) {
    return <Navigate to={resolvePostLoginUrl(from, storedUser)} replace />;
  }

  return (
    <Card variant="outlined" className="auth-card-container">
      <CardContent>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item md={12} className="auth-header">
                  <PanelInfo className="auth-logo" />
                  <div className="auth-title">ورود به حساب کاربری</div>
                  <VpnLoginHint />
                </Grid>

                <Grid item md={12}>
                    <TextField
                    label="نام کاربری"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <UserIcon />
                        </InputAdornment>
                      ),
                    }}
                    name="username"
                    type="string"
                    value={values.username}
                    inputProps={{ "data-testid": "login-username" }}
                    onChange={(e) =>
                      setFieldValue("username", normalizeDigits(e.target.value))
                    }
                    error={errors.username && touched.username}
                    helperText={
                      errors.username && touched.username && errors.username
                    }
                  />
                </Grid>

                <Grid item md={12}>
                  <TextField
                    label="رمز عبور"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <UnlockIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={
                              showPassword ? "مخفی کردن رمز عبور" : "نمایش رمز عبور"
                            }
                            edge="end"
                            onClick={() => setShowPassword((prev) => !prev)}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={values.password}
                    inputProps={{ "data-testid": "login-password" }}
                    onChange={(e) =>
                      setFieldValue("password", normalizeDigits(e.target.value))
                    }
                    error={errors.password && touched.password}
                    helperText={
                      errors.password && touched.password && errors.password
                    }
                  />
                </Grid>

                <Grid item md={12} container justifyContent="flex-end">
                  <Button
                    variant="text"
                    onClick={() => onChangeLoginKind("Mobile")}
                    sx={{
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    }}
                  >
                    فراموشی رمز عبور
                  </Button>
                </Grid>

                <Grid item md={12}>
                  <Button
                    fullWidth
                    disabled={isSubmitting}
                    variant="contained"
                    type="submit"
                    size="large"
                    sx={{ gap: 1 }}
                    data-testid="login-submit"
                  >
                    {loading && <CircularProgress size={18} color="inherit" />}
                    ورود
                  </Button>
                </Grid>

                {hasBothLogin ? (
                  <Grid item md={12} container justifyContent={"center"}>
                    <Button
                      variant="text"
                      onClick={() => onChangeLoginKind("Mobile")}
                      sx={{
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                      }}
                    >
ثبت نام |                       ورود با پیامک
                    </Button>
                  </Grid>
                ) : null}
              </Grid>
            </form> 
          )}
        </Formik>

        {message && (
          <Alert sx={{ mt: 3 }} severity={getMessageSeverity(message)}>
            {getMessageText(message)}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default UserPassLogin;
